import { COMPOUND_BY_ID, COMPOUNDS, type Compound } from '../lib/compounds';
import { GOAL_BY_ID } from '../lib/goals';
import { buildVendorUrl, amazonProductUrl } from '../lib/affiliate';
import { linkifyHtml } from '../lib/compound_linkifier';
import { addToActive } from '../lib/stack_store';
import { trackEvent, recordObservation } from '../lib/analytics';

// Vendor allowlist — only render affiliate buttons for programs that are paying
// us. Add a name here (matching the `name` field in compound JSON
// `affiliate_vendors`) when an affiliate program approves. Amazon is rendered
// separately above via amazon_asin + search URL and is always on.
const APPROVED_VENDORS = new Set<string>(['Amazon']);

export function renderCompound(params: Record<string, string>) {
  const app = document.getElementById('app')!;
  const c = COMPOUND_BY_ID[params.id];
  if (!c) {
    app.innerHTML = `<h2>Not found</h2><p>No compound at <code>/c/${esc(params.id)}</code>.</p><p><a href="/">Back home</a></p>`;
    return;
  }
  const stars = '★'.repeat(c.evidence_stars) + '<span class="stars-empty">' + '★'.repeat(5 - c.evidence_stars) + '</span>';
  const dose = c.typical_dose_mg.min === c.typical_dose_mg.max
    ? `${c.typical_dose_mg.min} mg`
    : `${c.typical_dose_mg.min}–${c.typical_dose_mg.max} mg`;

  app.innerHTML = `
    <div class="detail-header">
      <h1>${esc(c.display_name)}</h1>
      <div class="sci">${esc(c.scientific_name)}</div>
      <div class="tags" style="margin-top:8px;">
        ${c.goals.map((g) => {
          const goal = GOAL_BY_ID[g];
          return `<a class="tag" href="/g/${esc(g)}" style="text-decoration:none;">${goal ? goal.icon + ' ' + esc(goal.label) : esc(g)}</a>`;
        }).join('')}
      </div>
    </div>

    <section class="detail-section">
      <p>${linkifyHtml(c.description, COMPOUND_BY_ID, c.id)}</p>
    </section>

    <section class="detail-section">
      <h3>What the research says</h3>
      <div class="kv">
        <div class="k">Evidence</div>
        <div class="v"><span class="stars">${stars}</span> &nbsp; (our subjective summary of the literature)</div>
        <div class="k">Typical dose</div>
        <div class="v">${dose}</div>
        <div class="k">Timing</div>
        <div class="v">${c.timing.join(', ')}</div>
        <div class="k">Cycle?</div>
        <div class="v">${c.cycle_required ? esc(c.cycle_pattern || 'Yes') : 'Not required'}</div>
        ${c.aliases.length ? `<div class="k">Also called</div><div class="v">${c.aliases.map(esc).join(', ')}</div>` : ''}
      </div>
    </section>

    ${c.warnings.length ? `
      <section class="detail-section">
        <h3>Caveats</h3>
        <div class="warnings"><ul>${c.warnings.map((w) => `<li>${linkifyHtml(w, COMPOUND_BY_ID, c.id)}</li>`).join('')}</ul></div>
      </section>
    ` : ''}

    <section class="detail-section">
      <h3>Sources</h3>
      <div class="ref-pills">
        ${c.references.map((r) => {
          const label = (r.type || '').toUpperCase().replace(/_/g, '-');
          return `<a class="ref-pill" href="${esc(r.url)}" target="_blank" rel="noopener" title="${esc(r.note)}" data-ref-type="${esc(r.type || '')}" data-ref-url="${esc(r.url)}">↗ ${esc(label)}</a>`;
        }).join('')}
      </div>
      <p class="footer-disclosure" style="font-size:12px;margin-top:8px;">All descriptions on this page are paraphrased summaries. Tap a source pill above for the original.</p>
    </section>

    <section class="detail-section">
      <h3>Buy from</h3>
      <div class="vendor-row">
        ${c.amazon_asin ? `<a class="vendor-btn" href="${esc(amazonProductUrl(c.amazon_asin))}" target="_blank" rel="noopener nofollow sponsored" title="${esc(c.amazon_product_name || '')}">Amazon →</a>` : ''}
        <a class="vendor-btn" href="https://www.amazon.com/s?k=${encodeURIComponent(c.display_name + ' supplement')}&tag=aiministries-20" target="_blank" rel="noopener nofollow sponsored" title="Search Amazon for more brands of ${esc(c.display_name)}">Search Amazon →</a>
        ${c.affiliate_vendors.filter((v) => APPROVED_VENDORS.has(v.name)).map((v) => `<a class="vendor-btn" href="${esc(buildVendorUrl(v.url_template))}" target="_blank" rel="noopener nofollow sponsored">${esc(v.name)} →</a>`).join('')}
      </div>
      ${c.amazon_product_name ? `<div style="font-size:12px;color:var(--dim);margin-top:6px;">Amazon pick: ${esc(c.amazon_product_name)}</div>` : ''}
      <span class="vendor-disclosure">Vendor links may be affiliate. Your tracking data stays yours.</span>
    </section>

    ${stacksWellWithHtml(c)}

    <section class="detail-section">
      <button id="add-to-stack" class="btn btn-primary" style="width:100%;padding:12px;font-size:15px;">➕ Add to stack</button>
    </section>

    <p style="margin-top:24px;"><a href="/">← All compounds</a></p>
  `;
  wireAddToStack(c);
  trackEvent('compound_view', { compound_id: c.id });

  document.querySelectorAll<HTMLAnchorElement>('.ref-pill').forEach((a) => {
    a.addEventListener('click', () => {
      trackEvent('reference_click', {
        compound_id: c.id,
        ref_type: a.dataset.refType,
        ref_url: a.dataset.refUrl,
      });
    });
  });
  // Track clicks on the "Stacks well with" buy buttons separately so we can
  // see which related-compound recommendations actually convert.
  app.querySelectorAll('.related-buy').forEach((b) => {
    b.addEventListener('click', () => {
      trackEvent('amazon_click', {
        compound_id: (b as HTMLElement).dataset.id,
        kind: (b as HTMLElement).dataset.kind,
        from: 'related',
      });
    });
  });
  // Track every outbound vendor click (Amazon direct, Amazon search, iHerb, etc.)
  app.querySelectorAll('.vendor-btn').forEach((b) => {
    b.addEventListener('click', () => {
      const href = (b as HTMLAnchorElement).href || '';
      const kind = href.includes('/dp/') ? 'direct' : (href.includes('amazon.com/s?') ? 'search' : 'vendor');
      trackEvent('amazon_click', { compound_id: c.id, kind, host: new URL(href).host });
    });
  });
  // Dwell event on tab close / route change.
  const enteredAt = Date.now();
  const onLeave = () => {
    const sec = Math.floor((Date.now() - enteredAt) / 1000);
    if (sec >= 2) trackEvent('compound_dwell', { compound_id: c.id, seconds: sec });
    window.removeEventListener('beforeunload', onLeave);
    window.removeEventListener('popstate', onLeave);
  };
  window.addEventListener('beforeunload', onLeave);
  window.addEventListener('popstate', onLeave);
}

function stackSuggestions(c: Compound): Compound[] {
  const out: Compound[] = [];
  const seen = new Set<string>([c.id]);
  // 1) Curated first
  for (const id of (c.stacks_well_with || [])) {
    const cc = COMPOUND_BY_ID[id];
    if (cc && !seen.has(cc.id)) { out.push(cc); seen.add(cc.id); }
    if (out.length >= 5) return out;
  }
  if (out.length >= 5) return out;
  // 2) Goal-overlap fallback
  const goals = new Set(c.goals || []);
  if (goals.size === 0) return out;
  const cands: { c: Compound; score: number }[] = [];
  for (const o of COMPOUNDS) {
    if (seen.has(o.id) || !o.goals || o.goals.length === 0) continue;
    let shared = 0;
    for (const g of o.goals) if (goals.has(g)) shared++;
    if (shared < 2) continue;
    cands.push({ c: o, score: shared * 10 + o.evidence_stars });
  }
  cands.sort((a, b) => b.score - a.score);
  for (const x of cands) {
    if (out.length >= 5) break;
    out.push(x.c); seen.add(x.c.id);
  }
  return out;
}

function stacksWellWithHtml(c: Compound): string {
  const picks = stackSuggestions(c);
  if (picks.length === 0) return '';
  return `
    <section class="detail-section">
      <details>
        <summary style="cursor:pointer;color:var(--acc);font-weight:600;font-size:13px;list-style:none;padding:6px 0;">
          ▸ Stacks well with… (${picks.length})
        </summary>
        <div style="display:flex;flex-direction:column;gap:6px;margin-top:6px;">
          ${picks.map((p) => {
            const buyHref = p.amazon_asin
              ? `https://amazon.com/dp/${p.amazon_asin}?tag=aiministries-20`
              : `https://www.amazon.com/s?k=${encodeURIComponent(p.display_name + ' supplement')}&tag=aiministries-20`;
            return `<div style="padding:10px;background:var(--bg-2);border:1px solid var(--line);border-radius:8px;display:flex;align-items:center;gap:10px;">
              <a href="/c/${esc(p.id)}" style="flex:1;color:var(--ink);text-decoration:none;">
                <div style="font-weight:600;">${esc(p.display_name)}</div>
                ${p.goals.length > 0 ? `<div style="color:var(--dim);font-size:11px;">${'★'.repeat(p.evidence_stars)} · ${esc(p.goals.slice(0, 2).join(', '))}</div>` : ''}
              </a>
              <a class="related-buy" data-id="${esc(p.id)}" data-kind="${p.amazon_asin ? 'direct' : 'search'}"
                 href="${esc(buyHref)}" target="_blank" rel="noopener nofollow sponsored"
                 style="padding:4px 8px;background:rgba(95,196,128,0.15);color:var(--acc);border:1px solid var(--acc);border-radius:6px;font-size:12px;font-weight:700;text-decoration:none;">🛒</a>
            </div>`;
          }).join('')}
        </div>
      </details>
    </section>
  `;
}

function wireAddToStack(c: Compound) {
  document.getElementById('add-to-stack')?.addEventListener('click', () => openAtsModal(c));
}

function openAtsModal(c: Compound) {
  const sources = c.sources || ['Capsule'];
  const sourceOptions = c.source_dose_options;
  const sourceUnits = c.source_units;
  let source = sources[0] || '';
  let unit = (sourceUnits && sourceUnits[source]) || 'mg';
  const presetsFor = (src: string): number[] => {
    const explicit = sourceOptions && sourceOptions[src];
    if (explicit && explicit.length > 0) return explicit;
    const out = new Set<number>();
    const sd = (c.source_doses && c.source_doses[src]);
    if (sd != null) out.add(sd);
    const lo = c.typical_dose_mg?.min ?? 0;
    const hi = c.typical_dose_mg?.max ?? 0;
    if (lo > 0) out.add(lo);
    if (hi > 0 && hi !== lo) {
      const mid = (lo + hi) / 2;
      if (mid > 0) out.add(mid);
      out.add(hi);
    }
    return Array.from(out).sort((a, b) => a - b);
  };
  let presets: number[] = presetsFor(source);

  const back = document.createElement('div');
  back.className = 'ats-back';
  back.innerHTML = `
    <div class="ats-box">
      <h3>Add ${esc(c.display_name)} to a stack</h3>
      <label>Source</label>
      <select id="ats-source">
        ${sources.map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('')}
      </select>
      <div id="ats-presets-wrap"></div>
      <label>Dose</label>
      <div style="display:flex;gap:6px;">
        <input id="ats-dose" type="number" step="0.01" placeholder="e.g. 200" style="flex:1;" />
        <select id="ats-unit" style="width:80px;">
          ${['mg','mcg','g','IU'].map((u) => `<option value="${u}" ${u === unit ? 'selected' : ''}>${u}</option>`).join('')}
        </select>
      </div>
      ${(c.components && c.components.length) ? `
      <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;background:var(--bg-3);padding:10px;border-radius:6px;margin-top:10px;">
        <input type="checkbox" id="ats-track-components" checked />
        <span>
          <strong style="font-size:13px;">Also track individual ingredients</strong><br/>
          <span style="color:var(--dim);font-size:11px;">Adds ${c.components.map((b) => b.compound_id).join(', ')} as separate items so daily totals (caffeine etc.) are accurate.</span>
        </span>
      </label>
      ` : ''}
      <label>Time of day</label>
      <select id="ats-timing">
        ${['AM','midday','PM','evening','bedtime','pre-workout','post-workout','with food'].map((t) => `<option value="${t}">${t}</option>`).join('')}
      </select>
      <div class="ats-actions">
        <button id="ats-cancel" class="btn">Cancel</button>
        <button id="ats-save" class="btn btn-primary">Add</button>
      </div>
    </div>
  `;
  document.body.appendChild(back);

  const paintPresets = () => {
    const wrap = back.querySelector('#ats-presets-wrap') as HTMLElement;
    if (!presets.length) { wrap.innerHTML = ''; return; }
    wrap.innerHTML = `
      <label>Common strengths (tap to set dose)</label>
      <div style="display:flex;flex-wrap:wrap;gap:6px;">
        ${presets.map((mg, i) => {
          const valInUnit = unit === 'mcg' ? mg * 1000 : (unit === 'g' ? mg / 1000 : mg);
          return `<button class="btn preset-btn" data-mg="${mg}" data-i="${i}" style="padding:6px 12px;font-size:13px;">${trimNum(valInUnit)} ${unit}</button>`;
        }).join('')}
      </div>
    `;
    wrap.querySelectorAll('.preset-btn').forEach((b) => {
      b.addEventListener('click', () => {
        const mg = +((b as HTMLElement).dataset.mg || '0');
        const valInUnit = unit === 'mcg' ? mg * 1000 : (unit === 'g' ? mg / 1000 : mg);
        (back.querySelector('#ats-dose') as HTMLInputElement).value = trimNum(valInUnit);
      });
    });
  };
  paintPresets();

  back.querySelector('#ats-source')?.addEventListener('change', (e) => {
    source = (e.target as HTMLSelectElement).value;
    unit = (sourceUnits && sourceUnits[source]) || 'mg';
    presets = presetsFor(source);
    (back.querySelector('#ats-unit') as HTMLSelectElement).value = unit;
    paintPresets();
  });
  back.querySelector('#ats-unit')?.addEventListener('change', (e) => {
    unit = (e.target as HTMLSelectElement).value;
    paintPresets();
  });
  back.querySelector('#ats-cancel')?.addEventListener('click', () => back.remove());
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
  back.querySelector('#ats-save')?.addEventListener('click', async () => {
    const doseStr = (back.querySelector('#ats-dose') as HTMLInputElement).value.trim();
    const timing = (back.querySelector('#ats-timing') as HTMLSelectElement).value;
    const doseValue = doseStr === '' ? null : parseFloat(doseStr);
    let doseMg: number | null = null;
    if (doseValue != null) {
      if (unit === 'mg') doseMg = doseValue;
      else if (unit === 'g') doseMg = doseValue * 1000;
      else if (unit === 'mcg') doseMg = doseValue / 1000;
      else if (unit === 'IU') doseMg = doseValue;
    }
    await addToActive({
      compound_id: c.id,
      dose_mg: doseMg,
      dose_value: doseValue,
      unit,
      timing,
      notes: null,
      source,
      brand: null,
      active: true,
      from_blend: null,
    });
    const trackComponents = (back.querySelector('#ats-track-components') as HTMLInputElement | null)?.checked ?? false;
    if (trackComponents && c.components) {
      for (const comp of c.components) {
        await addToActive({
          compound_id: comp.compound_id,
          dose_mg: comp.dose_mg,
          dose_value: comp.dose_value ?? comp.dose_mg,
          unit: comp.unit ?? 'mg',
          timing,
          notes: null,
          source,
          brand: null,
          active: true,
          from_blend: c.id,
        });
      }
    }
    // Analytics: Tier 1 (consented usage) + Tier 2 (opt-in dose research).
    trackEvent('add_to_stack', { compound_id: c.id, source, unit, has_components: !!(c.components && c.components.length), tracked_components: trackComponents });
    if (doseMg != null) {
      recordObservation({ kind: 'dose', value: doseMg, unit: 'mg', compound_id: c.id, payload: { source, timing } });
    }
    back.remove();
    // Quick confirmation banner
    const toast = document.createElement('div');
    toast.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--acc);color:#000;padding:12px 18px;border-radius:8px;font-weight:700;z-index:10000;';
    toast.textContent = `Added ${c.display_name} to your stack`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2200);
  });
}

function trimNum(n: number): string {
  if (Math.abs(n - Math.round(n)) < 1e-9) return Math.round(n).toString();
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
